import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PieChart,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  RefreshCw,
  Filter,
  Download,
  Play,
  Pause,
  ChevronDown,
  ChevronRight,
  Users,
  DollarSign,
  Percent,
  Clock,
  Shield,
  Zap,
  Eye,
  Check,
  X
} from 'lucide-react';

// ============================================================================
// TYPES
// ============================================================================

interface ModelPortfolio {
  id: number;
  name: string;
  code: string;
  strategy: 'INCOME' | 'GROWTH' | 'BALANCED' | 'AGGRESSIVE' | 'CONSERVATIVE';
  riskLevel: number;
  targetReturn: number;
  aum: number;
  accountCount: number;
  driftThreshold: number;
  allocations: Allocation[];
}

interface Allocation {
  assetClass: string;
  targetPercent: number;
  currentPercent: number;
  minPercent: number;
  maxPercent: number;
}

interface AccountDrift {
  accountId: number;
  accountName: string;
  accountNumber: string;
  householdName: string;
  modelName: string;
  modelId: number;
  currentValue: number;
  driftPercent: number;
  driftAmount: number;
  daysOutOfSync: number;
  lastRebalanced: string;
  isTaxable: boolean;
  proposedTrades: ProposedTrade[];
}

interface ProposedTrade {
  id: string;
  securityId: string;
  symbol: string;
  securityName: string;
  side: 'BUY' | 'SELL';
  quantity: number;
  estimatedValue: number;
  assetClass: string;
  reason: string;
  taxImpact?: {
    shortTermGain?: number;
    longTermGain?: number;
    washSaleWarning?: boolean;
  };
}

interface RebalancePreview {
  totalAccounts: number;
  totalTrades: number;
  estimatedTurnover: number;
  estimatedTaxImpact: number;
  accounts: AccountDrift[];
}

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const Container = styled.div`
  padding: 2rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  min-height: calc(100vh - 64px);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Subtitle = styled.p`
  color: #64748b;
  margin: 0.5rem 0 0;
  font-size: 0.875rem;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'success' | 'warning' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          &:hover { background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); }
          &:disabled { opacity: 0.5; cursor: not-allowed; }
        `;
      case 'success':
        return `
          background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
          color: white;
          &:hover { background: linear-gradient(135deg, #16a34a 0%, #15803d 100%); }
          &:disabled { opacity: 0.5; cursor: not-allowed; }
        `;
      case 'warning':
        return `
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
          &:hover { background: linear-gradient(135deg, #d97706 0%, #b45309 100%); }
        `;
      default:
        return `
          background: rgba(255, 255, 255, 0.05);
          color: #e2e8f0;
          border: 1px solid rgba(255, 255, 255, 0.1);
          &:hover { background: rgba(255, 255, 255, 0.1); }
        `;
    }
  }}
`;

const ErrorBanner = styled.div`
  background: linear-gradient(135deg, rgba(234, 179, 8, 0.1) 0%, rgba(234, 179, 8, 0.05) 100%);
  border: 1px solid rgba(234, 179, 8, 0.3);
  border-radius: 8px;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #fbbf24;
  font-size: 0.875rem;

  svg {
    flex-shrink: 0;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div<{ variant?: 'warning' | 'success' | 'info' }>`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid ${props => {
    switch (props.variant) {
      case 'warning': return 'rgba(234, 179, 8, 0.3)';
      case 'success': return 'rgba(34, 197, 94, 0.3)';
      default: return 'rgba(255, 255, 255, 0.1)';
    }
  }};
  border-radius: 12px;
  padding: 1.25rem;
`;

const StatValue = styled.div<{ $color?: string }>`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${props => props.$color || '#ffffff'};
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FiltersBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 0.625rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #e2e8f0;
  font-size: 0.875rem;
  cursor: pointer;
  min-width: 180px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }

  option {
    background: #1e293b;
    color: #e2e8f0;
  }
`;

const SearchInput = styled.input`
  padding: 0.625rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #e2e8f0;
  font-size: 0.875rem;
  width: 300px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }

  &::placeholder {
    color: #64748b;
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const CardTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 0.875rem 1rem;
  font-size: 0.7rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const TableRow = styled.tr<{ $selected?: boolean }>`
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  background: ${props => props.$selected ? 'rgba(59, 130, 246, 0.1)' : 'transparent'};
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.03);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  font-size: 0.875rem;
  color: #e2e8f0;
  vertical-align: middle;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: #3b82f6;
  cursor: pointer;
`;

const DriftBadge = styled.span<{ $severity: 'low' | 'medium' | 'high' }>`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;

  ${props => {
    switch (props.$severity) {
      case 'high':
        return 'background: rgba(239, 68, 68, 0.2); color: #ef4444;';
      case 'medium':
        return 'background: rgba(234, 179, 8, 0.2); color: #eab308;';
      default:
        return 'background: rgba(34, 197, 94, 0.2); color: #22c55e;';
    }
  }}
`;

const ExpandButton = styled.button`
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  transition: color 0.15s;

  &:hover {
    color: #ffffff;
  }
`;

const TradePreviewRow = styled.tr`
  background: rgba(30, 41, 59, 0.5);
`;

const TradePreviewCell = styled.td`
  padding: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const TradePreviewContainer = styled.div`
  padding: 1rem 1rem 1rem 3rem;
`;

const TradeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TradeItem = styled.div<{ $side: 'BUY' | 'SELL' }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  border-left: 3px solid ${props => props.$side === 'BUY' ? '#22c55e' : '#ef4444'};
`;

const TradeSide = styled.span<{ $side: 'BUY' | 'SELL' }>`
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  color: ${props => props.$side === 'BUY' ? '#22c55e' : '#ef4444'};
  width: 35px;
`;

const TradeSymbol = styled.span`
  font-weight: 600;
  color: #ffffff;
  width: 80px;
`;

const TradeDetails = styled.span`
  color: #94a3b8;
  font-size: 0.875rem;
  flex: 1;
`;

const TradeValue = styled.span`
  color: #ffffff;
  font-weight: 500;
`;

const TaxWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: rgba(234, 179, 8, 0.1);
  border-radius: 6px;
  font-size: 0.75rem;
  color: #eab308;
  margin-top: 0.75rem;
`;

const ExecutionPanel = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(15, 23, 42, 0.95);
  backdrop-filter: blur(12px);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 100;
`;

const ExecutionStats = styled.div`
  display: flex;
  gap: 2rem;
`;

const ExecutionStat = styled.div`
  text-align: center;
`;

const ExecutionStatValue = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: #ffffff;
`;

const ExecutionStatLabel = styled.div`
  font-size: 0.7rem;
  color: #64748b;
  text-transform: uppercase;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #64748b;
`;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatCurrency = (value: number) => {
  if (Math.abs(value) >= 1000000) {
    return `$${(value / 1000000).toFixed(2)}M`;
  }
  if (Math.abs(value) >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const getDriftSeverity = (drift: number, threshold: number = 5): 'low' | 'medium' | 'high' => {
  const ratio = Math.abs(drift) / threshold;
  if (ratio >= 1.5) return 'high';
  if (ratio >= 1) return 'medium';
  return 'low';
};

// ============================================================================
// COMPONENT
// ============================================================================

const ModelTradingScreen: React.FC = () => {
  const [models, setModels] = useState<ModelPortfolio[]>([]);
  const [accountDrifts, setAccountDrifts] = useState<AccountDrift[]>([]);
  const [selectedModel, setSelectedModel] = useState<number | null>(null);
  const [selectedAccounts, setSelectedAccounts] = useState<Set<number>>(new Set());
  const [expandedAccounts, setExpandedAccounts] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [driftFilter, setDriftFilter] = useState<'all' | 'exceeds' | 'within'>('all');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch models and account drifts from backend
        const [modelsRes, driftsRes] = await Promise.all([
          axios.get('/api/advisor/models/all'),
          axios.get('/api/advisor/rebalancing/account-drifts')
        ]);

        // Transform models data
        const modelsData = modelsRes.data.map((m: any) => ({
          id: m.id,
          name: m.modelName,
          code: m.modelCode,
          strategy: m.riskProfile || 'BALANCED',
          riskLevel: 5,
          targetReturn: m.targetReturnPct || 7.0,
          aum: m.totalModelAum || 0,
          accountCount: m.assignedAccountCount || 0,
          driftThreshold: m.driftThresholdPct || 5,
          allocations: (m.allocations || []).map((a: any) => ({
            assetClass: a.assetClass || a.sector || a.cusip || 'Unknown',
            targetPercent: a.targetWeightPct || 0,
            currentPercent: a.targetWeightPct || 0, // Will be calculated from positions
            minPercent: a.minWeightPct || 0,
            maxPercent: a.maxWeightPct || 100,
          }))
        }));
        setModels(modelsData);

        // Transform account drifts data
        const driftsData = driftsRes.data.map((d: any) => ({
          accountId: d.accountId,
          accountName: d.accountName,
          accountNumber: d.accountNumber || '',
          householdName: d.householdName || 'No Household',
          modelName: d.modelName,
          modelId: d.modelId,
          currentValue: parseFloat(d.currentValue) || 0,
          driftPercent: parseFloat(d.driftPercent) || 0,
          driftAmount: parseFloat(d.driftAmount) || 0,
          daysOutOfSync: d.daysOutOfSync || 0,
          lastRebalanced: d.lastRebalanced || '',
          isTaxable: d.taxable || false,
          proposedTrades: (d.proposedTrades || []).map((t: any) => ({
            id: t.id,
            securityId: t.securityId || '',
            symbol: t.symbol || t.cusip || '',
            securityName: t.securityName || t.symbol || '',
            side: t.side,
            quantity: parseFloat(t.quantity) || 0,
            estimatedValue: parseFloat(t.estimatedValue) || 0,
            assetClass: t.assetClass || '',
            reason: t.reason || '',
            taxImpact: t.taxImpact ? {
              shortTermGain: t.taxImpact.shortTermGain,
              longTermGain: t.taxImpact.longTermGain,
              washSaleWarning: t.taxImpact.washSaleWarning
            } : undefined
          }))
        }));
        setAccountDrifts(driftsData);

      } catch (err: any) {
        console.error('Error loading Model Trading data:', err);
        const errorMsg = err.response?.data?.message || err.message || 'Failed to load data';
        setError(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter accounts based on selection
  const filteredAccounts = useMemo(() => {
    return accountDrifts.filter(account => {
      // Model filter
      if (selectedModel && account.modelId !== selectedModel) return false;

      // Drift filter
      const threshold = models.find(m => m.id === account.modelId)?.driftThreshold || 5;
      if (driftFilter === 'exceeds' && account.driftPercent <= threshold) return false;
      if (driftFilter === 'within' && account.driftPercent > threshold) return false;

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          account.accountName.toLowerCase().includes(query) ||
          account.accountNumber.toLowerCase().includes(query) ||
          account.householdName.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [accountDrifts, selectedModel, driftFilter, searchQuery, models]);

  // Statistics
  const stats = useMemo(() => {
    const exceedingThreshold = accountDrifts.filter(a => {
      const threshold = models.find(m => m.id === a.modelId)?.driftThreshold || 5;
      return a.driftPercent > threshold;
    });

    const selectedAccountsList = filteredAccounts.filter(a => selectedAccounts.has(a.accountId));
    const totalTrades = selectedAccountsList.reduce((sum, a) => sum + a.proposedTrades.length, 0);
    const totalValue = selectedAccountsList.reduce((sum, a) => {
      return sum + a.proposedTrades.reduce((ts, t) => ts + t.estimatedValue, 0);
    }, 0);

    return {
      totalAccounts: accountDrifts.length,
      exceedingThreshold: exceedingThreshold.length,
      selectedCount: selectedAccounts.size,
      totalTrades,
      totalValue
    };
  }, [accountDrifts, models, filteredAccounts, selectedAccounts]);

  const toggleAccountSelection = (accountId: number) => {
    const newSelected = new Set(selectedAccounts);
    if (newSelected.has(accountId)) {
      newSelected.delete(accountId);
    } else {
      newSelected.add(accountId);
    }
    setSelectedAccounts(newSelected);
  };

  const toggleAccountExpand = (accountId: number) => {
    const newExpanded = new Set(expandedAccounts);
    if (newExpanded.has(accountId)) {
      newExpanded.delete(accountId);
    } else {
      newExpanded.add(accountId);
    }
    setExpandedAccounts(newExpanded);
  };

  const selectAll = () => {
    const allIds = new Set(filteredAccounts.map(a => a.accountId));
    setSelectedAccounts(allIds);
  };

  const selectNone = () => {
    setSelectedAccounts(new Set());
  };

  const selectExceedingThreshold = () => {
    const exceedingIds = new Set(
      filteredAccounts
        .filter(a => {
          const threshold = models.find(m => m.id === a.modelId)?.driftThreshold || 5;
          return a.driftPercent > threshold;
        })
        .map(a => a.accountId)
    );
    setSelectedAccounts(exceedingIds);
  };

  const handleGenerateTrades = async () => {
    if (selectedAccounts.size === 0) return;

    setGenerating(true);
    try {
      // Call batch rebalance endpoint to create draft trades
      const response = await axios.post('/api/advisor/rebalancing/batch', {
        accountIds: Array.from(selectedAccounts),
        generateDraftTrades: true,
        respectTaxLots: true,
        executionMode: 'EXECUTE' // EXECUTE creates actual draft trades, PREVIEW just shows what would happen
      });

      const result = response.data;
      alert(`Successfully generated ${result.totalTrades} draft trades for ${result.totalAccounts} accounts.\n\nTotal trade value: ${formatCurrency(result.totalTradeValue)}\n\nThese trades are now in DRAFT status and require approval.`);

      // Refresh the data to show updated drift status
      const driftsRes = await axios.get('/api/advisor/rebalancing/account-drifts');
      const driftsData = driftsRes.data.map((d: any) => ({
        accountId: d.accountId,
        accountName: d.accountName,
        accountNumber: d.accountNumber || '',
        householdName: d.householdName || 'No Household',
        modelName: d.modelName,
        modelId: d.modelId,
        currentValue: parseFloat(d.currentValue) || 0,
        driftPercent: parseFloat(d.driftPercent) || 0,
        driftAmount: parseFloat(d.driftAmount) || 0,
        daysOutOfSync: d.daysOutOfSync || 0,
        lastRebalanced: d.lastRebalanced || '',
        isTaxable: d.taxable || false,
        proposedTrades: (d.proposedTrades || []).map((t: any) => ({
          id: t.id,
          securityId: t.securityId || '',
          symbol: t.symbol || t.cusip || '',
          securityName: t.securityName || t.symbol || '',
          side: t.side,
          quantity: parseFloat(t.quantity) || 0,
          estimatedValue: parseFloat(t.estimatedValue) || 0,
          assetClass: t.assetClass || '',
          reason: t.reason || '',
          taxImpact: t.taxImpact
        }))
      }));
      setAccountDrifts(driftsData);
      setSelectedAccounts(new Set());

    } catch (error: any) {
      console.error('Error generating trades:', error);
      // Show error message
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
      alert(`Error generating trades: ${errorMsg}\n\nPlease check that accounts have valid model assignments and positions.`);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <EmptyState>Loading model trading data...</EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <div>
          <Title>
            <PieChart className="w-7 h-7 text-blue-400" />
            Model Trading
          </Title>
          <Subtitle>Generate rebalancing trades to align accounts with their assigned models</Subtitle>
        </div>
        <HeaderActions>
          <Button onClick={() => setDriftFilter('exceeds')} variant={driftFilter === 'exceeds' ? 'warning' : 'secondary'}>
            <AlertTriangle className="w-4 h-4" />
            Exceeds Threshold Only
          </Button>
          <Button
            onClick={handleGenerateTrades}
            variant="success"
            disabled={selectedAccounts.size === 0 || generating}
            title="Generate rebalancing trades for selected accounts"
          >
            {generating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Generate {stats.totalTrades} Trades
          </Button>
        </HeaderActions>
      </Header>

      {error && (
        <ErrorBanner>
          <AlertTriangle className="w-5 h-5" />
          <div>
            <strong>Error:</strong> {error}
          </div>
        </ErrorBanner>
      )}

      <StatsGrid>
        <StatCard>
          <StatValue>{stats.totalAccounts}</StatValue>
          <StatLabel>Total Accounts</StatLabel>
        </StatCard>
        <StatCard variant={stats.exceedingThreshold > 0 ? 'warning' : 'success'}>
          <StatValue $color={stats.exceedingThreshold > 0 ? '#eab308' : '#22c55e'}>
            {stats.exceedingThreshold}
          </StatValue>
          <StatLabel>Exceeding Threshold</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue $color="#3b82f6">{stats.selectedCount}</StatValue>
          <StatLabel>Selected for Rebalance</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{formatCurrency(stats.totalValue)}</StatValue>
          <StatLabel>Estimated Trade Value</StatLabel>
        </StatCard>
      </StatsGrid>

      <FiltersBar>
        <FilterSelect value={selectedModel || ''} onChange={e => setSelectedModel(e.target.value ? Number(e.target.value) : null)}>
          <option value="">All Models</option>
          {models.map(model => (
            <option key={model.id} value={model.id}>{model.name} ({model.accountCount})</option>
          ))}
        </FilterSelect>
        <FilterSelect value={driftFilter} onChange={e => setDriftFilter(e.target.value as any)}>
          <option value="all">All Drift Levels</option>
          <option value="exceeds">Exceeds Threshold</option>
          <option value="within">Within Threshold</option>
        </FilterSelect>
        <SearchInput
          type="text"
          placeholder="Search accounts, households..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        <Button onClick={selectAll}>Select All</Button>
        <Button onClick={selectNone}>Clear Selection</Button>
        <Button variant="warning" onClick={selectExceedingThreshold}>
          Select All Exceeding
        </Button>
      </FiltersBar>

      <Card>
        <CardHeader>
          <CardTitle>
            <Users className="w-4 h-4 text-blue-400" />
            Account Drift Analysis
          </CardTitle>
          <span style={{ color: '#64748b', fontSize: '0.875rem' }}>
            {filteredAccounts.length} accounts
          </span>
        </CardHeader>
        <Table>
          <thead>
            <tr>
              <TableHeader style={{ width: 50 }}></TableHeader>
              <TableHeader>Account</TableHeader>
              <TableHeader>Household</TableHeader>
              <TableHeader>Model</TableHeader>
              <TableHeader>Value</TableHeader>
              <TableHeader>Drift</TableHeader>
              <TableHeader>Days Out</TableHeader>
              <TableHeader>Trades</TableHeader>
              <TableHeader></TableHeader>
            </tr>
          </thead>
          <tbody>
            {filteredAccounts.map(account => {
              const threshold = models.find(m => m.id === account.modelId)?.driftThreshold || 5;
              const isExpanded = expandedAccounts.has(account.accountId);
              const isSelected = selectedAccounts.has(account.accountId);

              return (
                <React.Fragment key={account.accountId}>
                  <TableRow $selected={isSelected}>
                    <TableCell>
                      <Checkbox
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleAccountSelection(account.accountId)}
                      />
                    </TableCell>
                    <TableCell>
                      <div style={{ fontWeight: 600, color: '#ffffff' }}>{account.accountName}</div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                        {account.accountNumber}
                        {account.isTaxable && (
                          <span style={{ marginLeft: '0.5rem', padding: '0.125rem 0.375rem', background: 'rgba(234, 179, 8, 0.1)', borderRadius: '4px', color: '#eab308' }}>
                            Taxable
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{account.householdName}</TableCell>
                    <TableCell>
                      <span style={{ padding: '0.25rem 0.5rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '4px', fontSize: '0.75rem', color: '#3b82f6' }}>
                        {account.modelName}
                      </span>
                    </TableCell>
                    <TableCell>{formatCurrency(account.currentValue)}</TableCell>
                    <TableCell>
                      <DriftBadge $severity={getDriftSeverity(account.driftPercent, threshold)}>
                        {account.driftPercent.toFixed(1)}%
                      </DriftBadge>
                    </TableCell>
                    <TableCell>
                      <span style={{ color: account.daysOutOfSync > 30 ? '#ef4444' : '#94a3b8' }}>
                        {account.daysOutOfSync} days
                      </span>
                    </TableCell>
                    <TableCell>{account.proposedTrades.length}</TableCell>
                    <TableCell>
                      <ExpandButton onClick={() => toggleAccountExpand(account.accountId)}>
                        {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                      </ExpandButton>
                    </TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TradePreviewRow>
                      <TradePreviewCell colSpan={9}>
                        <TradePreviewContainer>
                          <div style={{ marginBottom: '0.75rem', fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Proposed Trades
                          </div>
                          <TradeList>
                            {account.proposedTrades.map(trade => (
                              <TradeItem key={trade.id} $side={trade.side}>
                                <TradeSide $side={trade.side}>{trade.side}</TradeSide>
                                <TradeSymbol>{trade.symbol}</TradeSymbol>
                                <TradeDetails>
                                  {trade.securityName} • {trade.quantity} shares • {trade.assetClass}
                                </TradeDetails>
                                <TradeValue>{formatCurrency(trade.estimatedValue)}</TradeValue>
                              </TradeItem>
                            ))}
                          </TradeList>
                          {account.proposedTrades.some(t => t.taxImpact?.washSaleWarning) && (
                            <TaxWarning>
                              <AlertTriangle className="w-4 h-4" />
                              Potential wash sale warning - similar security sold within 30 days
                            </TaxWarning>
                          )}
                        </TradePreviewContainer>
                      </TradePreviewCell>
                    </TradePreviewRow>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      </Card>

      {selectedAccounts.size > 0 && (
        <ExecutionPanel>
          <ExecutionStats>
            <ExecutionStat>
              <ExecutionStatValue>{stats.selectedCount}</ExecutionStatValue>
              <ExecutionStatLabel>Accounts</ExecutionStatLabel>
            </ExecutionStat>
            <ExecutionStat>
              <ExecutionStatValue>{stats.totalTrades}</ExecutionStatValue>
              <ExecutionStatLabel>Trades</ExecutionStatLabel>
            </ExecutionStat>
            <ExecutionStat>
              <ExecutionStatValue>{formatCurrency(stats.totalValue)}</ExecutionStatValue>
              <ExecutionStatLabel>Total Value</ExecutionStatLabel>
            </ExecutionStat>
          </ExecutionStats>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <Button onClick={selectNone}>
              <X className="w-4 h-4" />
              Cancel
            </Button>
            <Button variant="primary" onClick={() => setExpandedAccounts(new Set(filteredAccounts.filter(a => selectedAccounts.has(a.accountId)).map(a => a.accountId)))}>
              <Eye className="w-4 h-4" />
              Preview All
            </Button>
            <Button
              variant="success"
              onClick={handleGenerateTrades}
              disabled={generating}
            >
              {generating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              Generate Draft Trades
            </Button>
          </div>
        </ExecutionPanel>
      )}
    </Container>
  );
};

export default ModelTradingScreen;
